import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { CLICK_ACTION, SAVE_PRIMARY_CONTACT } from '../../../constants/tagManager';
import type {
  IContactDraftFields,
  ICreateProspectContact,
  IProspect,
  IProspectContact,
  IUpdateProspectContact
} from '../../../interfaces/prospects';
import TagManagerService from '../../../services/gtm/tagManagerService';
import { TRANSLATIONS_PROSPECTS } from '../../../translations/prospects';
import { ProspectContactType } from '../../../types/prospects';
import { getPrimaryContact } from '../../../utils/prospects/prospects';
import { isDraftEqual, trimmedOrUndefined } from '../../../utils/utils';
import { validateEmail } from '../../../utils/validation/validation';
import ContactDetails from '../ContactDetails/ContactDetails';
import ContactFields from '../ContactFields/ContactFields';

interface IPrimaryContactCardProps {
  prospect: IProspect;
  onCreateContact: (input: ICreateProspectContact) => Promise<void>;
  onUpdateContact: (contactId: number, input: IUpdateProspectContact) => Promise<void>;
  onDraftChange?: (draft: IContactDraftFields | null) => void;
  editing: boolean;
  onValidChange: (valid: boolean) => void;
  billingIsSameAsPrimary?: boolean;
  disabled?: boolean;
}

const draftFromContact = (contact?: IProspectContact): IContactDraftFields => ({
  name: contact?.name ?? '',
  title: contact?.title ?? '',
  email: contact?.email ?? '',
  phone: contact?.phone ?? ''
});

const buildUpdateFields = (draft: IContactDraftFields): IUpdateProspectContact => ({
  name: trimmedOrUndefined(draft.name),
  title: trimmedOrUndefined(draft.title),
  email: trimmedOrUndefined(draft.email),
  phone: trimmedOrUndefined(draft.phone)
});

const buildCreateFields = (draft: IContactDraftFields): ICreateProspectContact => ({
  contactType: ProspectContactType.Administrative,
  name: draft.name.trim(),
  title: trimmedOrUndefined(draft.title),
  email: trimmedOrUndefined(draft.email),
  phone: trimmedOrUndefined(draft.phone)
});

const PrimaryContactCard = forwardRef<
  { save: () => Promise<void>; getDraft: () => IContactDraftFields },
  IPrimaryContactCardProps
>(
  (
    {
      prospect,
      onCreateContact,
      onUpdateContact,
      onDraftChange,
      editing,
      onValidChange,
      billingIsSameAsPrimary = true,
      disabled = false
    },
    ref
  ) => {
    const { t } = useTranslation();
    const contact = getPrimaryContact(prospect);
    const [draft, setDraft] = useState<IContactDraftFields>(() => draftFromContact(contact));

    useEffect(() => {
      onDraftChange?.(editing ? draft : null);
    }, [editing, draft, onDraftChange]);

    const emailInvalid = !!draft.email.trim() && !validateEmail(draft.email.trim());
    const emailValid = !!draft.email.trim() && !emailInvalid;
    const allFieldsValid = emailValid && !!draft.name.trim() && !!draft.title.trim() && !!draft.phone.trim();
    const requiredFieldsValid = billingIsSameAsPrimary ? allFieldsValid : emailValid;
    const isValid = !!contact || requiredFieldsValid;

    useEffect(() => {
      onValidChange(isValid);
    }, [isValid, onValidChange]);

    useImperativeHandle(
      ref,
      () => ({
        save: async () => {
          if (isDraftEqual(draft, draftFromContact(contact))) return;
          if (contact) {
            await onUpdateContact(contact.id, buildUpdateFields(draft));
          } else {
            await onCreateContact(buildCreateFields(draft));
          }
          TagManagerService.pushEvent(CLICK_ACTION, SAVE_PRIMARY_CONTACT);
        },
        getDraft: () => draft
      }),
      [contact, draft, onCreateContact, onUpdateContact]
    );

    const set = (key: keyof IContactDraftFields, value: string) => setDraft((prev) => ({ ...prev, [key]: value }));

    if (editing) {
      return (
        <div className="profile-form">
          <ContactFields
            draft={draft}
            onChange={set}
            showTitle
            namePlaceholder={t(TRANSLATIONS_PROSPECTS.forms.contactName.placeholder)}
            emailPlaceholder={t(TRANSLATIONS_PROSPECTS.forms.contactEmail.placeholder)}
            emailRequired
            nameRequired={billingIsSameAsPrimary}
            requiredTitle={billingIsSameAsPrimary}
            requiredPhone={billingIsSameAsPrimary}
            disabled={disabled}
          />
        </div>
      );
    }

    if (!contact) {
      return <p className="profile-card__empty-hint">{t(TRANSLATIONS_PROSPECTS.profile.primaryContact.empty)}</p>;
    }

    return <ContactDetails contact={contact} showTitle />;
  }
);

PrimaryContactCard.displayName = 'PrimaryContactCard';
export default PrimaryContactCard;
